<template>
    <div
        :class="['desc-render', 'content-box', 'highlight-box', 'desc-box', 'clearfix', `desc-compare-${side}`, {
            'has-catalogs': catalogVisible && (!compareData.enabled || compareData.recover),
            'smart-image-feature__open': textImageFeatureStatus,
            'desc-compare-render': isCompareView
        }]"
    >
        <ResizeObserver @notify="setTranslationAlign" />
        <template v-if="showLoading">
            <CompareLoadingContent v-if="isShowCompareLoading" class="pa-mt-[92px]" />
            <div v-else flex class="loading-box center">
                <Loading />
            </div>
        </template>
        <template v-if="!defaultLanguageEqualRootLang">
            <!-- 双视图下 额外添加一层翻译开启的控制 -->
            <SideViewSelfTranslationToggle
                v-if="!showLoading && !inSingleView && alterationMode && !isCompareView && !compareData.enabled"
                :value="isOpenedSelfTransInDual"
                :side="side"
                style="margin-bottom: 16px;"
                @change="toggleSideViewSelfTrans"
            />
        </template>
        <nav v-if="!isCompareView" class="patsnap-nav-tabs-wrap clearfix">
            <div class="patsnap-patent-handle-wrap desc__view-handle">
                <ElTooltip
                    v-if="hasSmartImageToggle"
                    class="desc__view-handle__item"
                    :visibleArrow="false"
                    :content="textImageFeatureButtonTips"
                    placement="top"
                    effect="dark"
                >
                    <button
                        class="pt-ui-btn"
                        :data-type="textImageFeatureStatus ? 'primary' : 'default'"
                        data-size="medium"
                        @click="toggleTextImageFeature"
                    >
                        <PtIcon icon="SolidImageHighlight" />
                    </button>
                </ElTooltip>
                <CompareSelect
                    v-if="hasCompareSelect"
                    class="desc-select desc__view-handle__item"
                    :class="{'active': compareData.enabled && !compareData.recover}"
                    :data="compareData"
                    :patentId="patentId"
                    @idxChange="compareTargetIdxChange"
                    @enabledChange="compareEnabledChange"
                />
            </div>
        </nav>
        <section
            v-show="!showLoading"
            class="desc-scroll clearfix"
            :class="{ 'mt-8': compareData.fullList.length || hasSmartImageToggle }"
            data-density="fullHeight"
        >
            <template v-if="(!compareData.enabled || compareData.recover)">
                <div
                    v-if="patent.RELATED_PATENT && patent.RELATED_PATENT.desc"
                    class="not-available"
                    v-html="
                        '(' +
                            $t('REPLACE_DESC', {
                                curPN: patent.PN,
                                replacePN: patent.RELATED_PATENT.desc.pn
                            }) +
                            ')'
                    "
                />
                <div v-if="descSource.isOCR" class="not-available">
                    {{ $t('ocrTip') }}
                </div>
                <PtSection
                    v-if="defaultOfDesc && isCompareView"
                    :class="{
                        'orgin_desc': true,
                        'desc-content-item': true,
                        'desc-render__desc-container': true,
                        'click2img-activated': click2figPartActivated,
                    }"
                    :text="defaultOfDesc"
                    :onHighlightComplete="() => { bindLazyLoadImg(); resetWordingFromCache(); }"
                    @click="articleClicked"
                />
                <template v-else>
                    <!-- 非双视图翻译状态下显示 -->
                    <PtSection
                        v-if="defaultOfDesc && !isDualTrans"
                        :class="{
                            'orgin_desc': true,
                            'desc-content-item': true,
                            'desc-render__desc-container': true,
                            'click2img-activated': click2figPartActivated,
                            'trans_open': isOpenTran,
                        }"
                        :text="defaultOfDesc"
                        :onHighlightComplete="() => { bindLazyLoadImg(); resetWordingFromCache(); setTranslationAlign(); }"
                        @click="articleClicked"
                    />
                    <TranslateBlock
                        v-if="defaultOfDesc && isOpenTran"
                        :key="rootLang + patentId + 'DESC'"
                        class="trans trans_desc"
                        :onHighlightComplete="() => { bindLazyLoadImg(); setTranslationAlign(); }"
                        field="DESC"
                        :patentId="patentId"
                        :sourceLang="langDefaultDesc"
                        :targetLang="rootLang"
                        :side="side"
                        :textLang="sideTransStatus[side] ? rootLang : langDefaultDesc"
                        :wordingPermission="wordingPermission"
                        :wordingKey="transWordingKey"
                        :wordingParams="transWordingParams"
                        @patsnapTranslateComplete="setTranslationAlign"
                        @patsnapTranslateFail="removeTranslationAlign"
                        @click.native="articleClicked"
                    />
                </template>
                <PtNodata v-if="!defaultOfDesc" :nodata-info="[$t('NO_DESC')]" class="no-data" />
                <div
                    ref="dualFiguresTip"
                    class="tip__arrow dual-figures_tip"
                >
                    <span>{{ curFigureContentTargetInfo.index+1 }}/{{ curFigureContentTargetInfo.length }}</span>
                    <button
                        type="button"
                        @click="toFigContent(curFigureContentTargetInfo.figure, -1)"
                    >
                        <PtIcon icon="SolidArrowUp" style="fill:#fff" />
                    </button>
                    <button
                        type="button"
                        @click="toFigContent(curFigureContentTargetInfo.figure, +1)"
                    >
                        <PtIcon icon="SolidArrowDown" style="fill:#fff" />
                    </button>
                    <button
                        type="button"
                        @click="closeFigContentTip"
                    >
                        <PtIcon icon="SolidCloseMedium" style="fill:#fff" />
                    </button>
                    <div class="arrow" data-popper-arrow />
                </div>
            </template>
            <TextCompare
                v-if="compareData.enabled && compareData.target && !compareData.recover"
                :key="`compareData_${compareData.target.patent_id}_${patentId}`"
                :to="patentId"
                :from="compareData.target.patent_id"
                :service="compareService"
                @close="closeCompare"
            >
                <template #tip>
                    <div
                        v-if="patent.RELATED_PATENT && patent.RELATED_PATENT.desc"
                        class="not-available"
                        v-html="
                            '(' +
                                $t('REPLACE_DESC', {
                                    curPN: patent.PN,
                                    replacePN: patent.RELATED_PATENT.desc.pn
                                }) +
                                ')'
                        "
                    />
                </template>
            </TextCompare>
        </section>
        <ImagePreviewDialog
            v-if="defaultOfDesc"
            :preProcessHook="previewHook"
            :descImage240List="DescImage240List"
            :descImageList="DescImageList"
            :container="container"
            @showImagePanel="showImagePanel"
        />

        <!-- 有數據，且非雙視圖翻譯模式下可以顯示 -->
        <CatalogOfDescription
            v-show="catalogVisible && (!compareData.enabled || compareData.recover)"
            :catalogs="catalogs"
            :deformable="inSingleView"
        />

        <!-- 智能附图卡片 -->
        <SmartImagePopover
            v-if="showSmartImage"
            ref="smartImagePopover"
            v-clickoutside="hidePopover"
            :labelName="labelName"
            :labelId="labelId"
            :patentId="patentId"
            :code="code"
            :isCompare="isCompareView"
            :side="side"
            :labelLang="labelLang"
            @close="hidePopover"
            @showSmartPanel="showSmartPanel"
        />
        <!-- 智能附图panel -->
        <div v-if="showSmartImagePanel" class="desc-smart-image-panel">
            <ImagePanelV3
                v-bind="smartPanelProps"
                :figureTextVisible.sync="smartPanelProps.figureTextVisible"
                :fetchFigures="smartPanelProps.fetchFigures"
                :imageData="smartPanelProps.imageData"
                :imagePanelShow="smartPanelProps.imagePanelShow"
                :showFigures="true"
                :figureTextFocusedId="smartPanelProps.figureTextFocusedId"
                :allowImageDownload="true"
                @image-panel-close="onSmartPanelClose"
            />
        </div>

        <DomNodesTip :wrapperRef="$el" :label="$t('patent-view.DESC')" />
    </div>
</template>

<script>
import Loading from '../../components/Loading';
import _ from 'lodash';
import $ from 'jquery';
import highlightDensityMixin from '../../mixins/highlightDensityMixin';
import PtSection from '@/components/PtSection';
import ImagePanelV3 from 'library-@/components/image-panel-v3';
import ImagePreviewDialog from '@patsnap-biz/image-preview-dialog';
import '@patsnap-biz/image-preview-dialog/lib/style';
import TranslateBlock from '../../components/translation-block/index';
import DescriptionService from '../../services/description-service';
import serviceInjectMixin from '@/mixins/serviceInject';
import ParagraphHighlight from '../../lib/paragraph-highlight';
import WordingService from '../../services/wording-service';
import { HIGHLIGHT_CACHE_KEY_IN_DESC, SEMANTIC_SUPPORTED_LANG, DESC_COMPARE_SERVICE, HIGHLIGHT_CACHE_KEY_IN_DESC_TRANS } from '../../constants';
import WordingHandler from '../../utils/wording-handler';
import CommentService from '../../services/comment-service';
import getParams from 'library-@/utils/get-params';
import { Toast, Icon } from 'patsnap-biz';
import dualFiguresRemotes from '@/mixins/text.remote.dualFigures.mixin.js';
import CatalogOfDescription from '../../components/catalog-of-description';
import { getStructureLink } from 'library-@/utils/formatStructureDesc';
import { TextAndGraphMixin } from '../../mixins/text-and-graph-mixin';
import SideViewSelfTranslationToggle from '../../components/SideViewSelfTranslationToggle';
import { setAlignStyle, removeAlignStyle } from '@/utils/paragraphAlignment';
import { ResizeObserver } from 'vue-resize';
import CompareSelect from '@/components/family-patent-compare/CompareSelect';
import TextCompare from '@/components/family-patent-compare/TextCompare';
import textCompareMixin from '@/components/family-patent-compare/textCompareMixin';
import QuickCommentService from '@/services/quick-comment-service';
import fontSizeAdjustHeightMixin from '../../components/patsnap-lab/font-size/fontSizeAdjustHeightMixin';
import TextSmartImageMixin from '@/mixins/textSmartImageMixin';
import SmartImagePopover from '@/components/smart-image-popover/SmartImagePopover';
import { Tooltip } from 'element-ui';
import { useCompareUrlState } from '@/composables/use-compare-url-state';
import '@patsnap-ui/icon/assets/solid/ImageHighlight.svg';
import CompareLoadingContent from '@/components/compare-components/compare-loading-content/CompareLoadingContent.vue';
import ComparePatentService from '../../services/compare-patent-service';
import { useCompareInit } from '@/composables/use-compare-init';
import { useSimilarParagraph } from '@/composables/use-similar-paragraph';
import { useSimilarParaScroll } from '@/composables/use-similar-para-scroll';
import { useCompareWording } from '@/composables/use-compare-wording';
import { generateUniqueId } from '../../utils/index';
import { computed } from 'vue';
import { useTranslationStates } from '@/composables/use-translation-states';
import { useDualViewStates } from '@/composables/use-dual-view-states';
import { usePermission } from '@/composables/use-permission';
import { useCommentDialog } from '@/components/dialog/CommentDialog/composables/use-comment-dialog';
import { usePatentBasic } from '@/composables/use-patent-basic';
import DomNodesTip from '@/components/dom-nodes-tip/DomNodesTip.vue';

const { get, debounce } = _;

/**
 * 結構化說明書
 */
const CatalogOfDescriptionMixin = {
    components: {
        CatalogOfDescription,
        PtIcon: Icon,
    },
    data() {
        return {
            sections: [],
        };
    },
    computed: {
        catalogs() {
            return getStructureLink(this.sections);
        },
        catalogVisible() {
            return this.catalogs && this.catalogs.length > 1 && !this.isDualTrans;
        },
        hasCompareSelect() {
            return this.permissions.textCompare
                && this.defaultOfDesc
                && this.compareData.fullList.length;
        },
    },
};

export default {
    name: 'PsDescription',
    components: {
        ElTooltip: Tooltip,
        Loading,
        PtSection,
        ImagePanelV3,
        ImagePreviewDialog,
        TranslateBlock,
        SideViewSelfTranslationToggle,
        ResizeObserver,
        CompareSelect,
        TextCompare,
        SmartImagePopover,
        CompareLoadingContent,
        DomNodesTip,
    },
    mixins: [
        dualFiguresRemotes,
        serviceInjectMixin,
        highlightDensityMixin,
        CatalogOfDescriptionMixin,
        TextAndGraphMixin,
        textCompareMixin,
        fontSizeAdjustHeightMixin,
        TextSmartImageMixin,
    ],
    props: {
        patentId: {
            type: String,
            required: true,
        },
        viewParams: {
            type: Object,
            default: () => ({}),
        },
        neighborName: { type: String },
        neighborSide: { type: String },
        side: { type: String },
        selfName: { type: String },
    },
    setup() {
        const { isCompareView, isSourceCustom, compareId, rightId } = useCompareUrlState();
        const {
            deferred: compareDeferred,
            isStartInit: compareStartInit,
            compareLang,
            isSame,
            compareInitParams,
        } = useCompareInit();

        const {
            currentAlteration,
            currentTranslateLanguages,
            currentLanguageModes,
            sideTransStatus,
            setSideTransStatus,
            setUserPreferredSideTransStatus,
            syncSideTransStatusWithUserPreferred,
        } = useTranslationStates();

        const {
            setSimilarParaTagPosition,
            setSimilarWordingTagPosition,
            revertSimilarWordingTagPosition,
            scrollToSimilarPara,
        } = useSimilarParagraph();
        const {
            isScrollImmediately,
            changeScrollImmediately,
            getCurScrollInfo,
        } = useSimilarParaScroll();

        const {
            isCompareWordingStart,
            compareWordingCacheKey,
            enabledCompareWording,
            onCompareWording,
            changeCompareWordingStart,
            onShowWordingNotFoundBar,
        } = useCompareWording();

        const {
            leftRef,
            rightRef,
        } = useDualViewStates();

        const {
            permissions,
        } = usePermission();

        const {
            isShowingCommentBox,
            isShowingAddCommentBox,
            quickCommentList,
            setIsShowingCommentBox,
            setIsShowingAddCommentBox,
            setIsShowingEditQuickComment,
            updateQuickComment,
        } = useCommentDialog();

        const {
            getPatentBase,
        } = usePatentBasic();

        const isShowCompareLoading = computed(() => {
            return isCompareView.value && !isSame.value && !isSourceCustom.value;
        });

        return {
            isCompareView,
            compareTargetPatentId: rightId,
            isShowCompareLoading,
            compareDeferred,
            compareStartInit,
            compareId,
            compareLang,
            compareInitParams,

            isScrollImmediately,

            setSimilarParaTagPosition,
            setSimilarWordingTagPosition,
            revertSimilarWordingTagPosition,
            scrollToSimilarPara,

            changeScrollImmediately,
            getCurScrollInfo,

            isCompareWordingStart,
            compareWordingCacheKey,
            enabledCompareWording,
            onCompareWording,
            changeCompareWordingStart,
            onShowWordingNotFoundBar,

            currentAlteration,
            currentTranslateLanguages,
            currentLanguageModes,
            sideTransStatus,
            setSideTransStatus,
            setUserPreferredSideTransStatus,
            syncSideTransStatusWithUserPreferred,

            leftRef,
            rightRef,

            permissions,

            isShowingCommentBox,
            isShowingAddCommentBox,
            quickCommentList,
            setIsShowingCommentBox,
            setIsShowingAddCommentBox,
            setIsShowingEditQuickComment,
            updateQuickComment,

            getPatentBase,
        };
    },
    data() {
        return {
            showLoading: false,
            patent: {},
            defaultOfDesc: undefined,
            langDefaultDesc: undefined,
            panelShow: false,
            imagePanelData: {
                index: 0,
                list: [],
                total: 1,
                type: 'image',
            },
            descSource: {},
            transWordingKey: HIGHLIGHT_CACHE_KEY_IN_DESC_TRANS,
            wordingPermission: {},
        };
    },
    computed: {
        inSingleView() {
            return get(this.viewParams, 'isSingleView', true);
        },
        /**
         * 非雙視圖&翻譯
         */
        isDualTrans() {
            const isDual = !this.inSingleView;
            const isTran = this.alterationMode;
            const isSelfTran = this.isOpenedSelfTransInDual && !this.defaultLanguageEqualRootLang;
            return isDual && isTran && isSelfTran;
        },
        isOpenedSelfTransInDual() {
            return this.inSingleView ? true : get(this.sideTransStatus, this.side, false);
        },
        click2figPartActivated() {
            const dualFigRef = this.getDualFigRef();
            if (this.neighborName === 'figures' && dualFigRef && dualFigRef.enableSmartFigures) {
                return true;
            }
            return false;
        },
        isOpenTran() {
            return this.alterationMode && (!this.inSingleView ? this.isOpenedSelfTransInDual : true) && !this.defaultLanguageEqualRootLang;
        },
        rootLang() {
            return this.currentTranslateLanguages[this.side || 'left'];
        },
        rootLangType() {
            return this.currentLanguageModes[this.side || 'left'];
        },
        alterationMode() {
            return this.currentAlteration[this.side || 'left'];
        },
        descRelatedPatentId() {
            return get(this.patent, 'RELATED_PATENT.desc.patent_id');
        },
        wordingScrollContainer() {
            const isSingleView = get(this, '$attrs.viewParams.isSingleView');
            const $root = $(this.$el);
            return isSingleView ? $root.parent() : $root;
        },
        // 翻译划段相关参数
        transWordingParams() {
            return {
                source: 'description-translation',
                workspace_id: this.workspaceId,
                folder_id: this.folderId,
                related_patent_id: this.descRelatedPatentId,
                wordingScrollContainer: this.wordingScrollContainer,
            };
        },
        hasSmartImageToggle() {
            return this.showSmartImageToggle
                && !this.compareData.enabled
                && this.defaultOfDesc;
        },
        defaultLanguageEqualRootLang() {
            return this.langDefaultDesc === this.rootLang;
        },
        isCompareSourceColumn() {
            return this.side === 'left';
        },
    },
    watch: {
        defaultLanguageEqualRootLang: {
            immediate: true,
            handler(bool) {
                if (bool) {
                    this.setSideTransStatus({ side: this.side, isVisible: false });
                } else {
                    this.syncSideTransStatusWithUserPreferred();
                }
            },
        },
        rootLang: {
            immediate: true,
            handler() {
                this.initDensityBar();
            },
        },
        isShowingAddCommentBox(val) {
            if (!val) {
                this.cleanMockSelection();
            }
        },
        isShowingCommentBox(val) {
            if (!val) {
                this.cleanMockSelection();
            }
        },
        isOpenTran(val) {
            if (!val) this.removeTranslationAlign();
        },
        /**
         * 双视图翻译时，原文节点被删除，此时需去除监听，
         * 反之，则需要恢复监听
         */
        isDualTrans(value) {
            if (value) {
                this.disposeWording();
            } else {
                this.bindWording();
            }
        },
        'compareData.enabled'(bol) {
            if (bol) {
                this.emptyDensityData();
            } else {
                this.initDensityBar();
                this.bindWording();
            }
            window.rvTrack('Clicked DescCompare', { type: bol ? 'open' : 'close' });
        },
        catalogs(value) {
            this.$emit('catalogsUpdate', value);
        },
        compareStartInit: {
            async handler() {
                if (this.isCompareSourceColumn && this.isCompareWordingStart) return;
                if (this.compareStartInit && this.descriptionService?.completed) {
                    this.showLoading = true;
                    this.sections = [];
                    await this.compareDeferred.promise;
                    const { text, lang, DESC_SOURCE, DESC_SECTIONS } = await this.descriptionService.getPatentText({ lang: this.compareLang, type: 'DESC', initParams: this.compareInitParams });
                    this.defaultOfDesc = text;
                    this.langDefaultDesc = lang;
                    this.sections = DESC_SECTIONS;
                    this.descSource = DESC_SOURCE || {};
                    this.showLoading = false;
                    this.setAndScrollToSimilar();
                    if (this.isCompareSourceColumn) {
                        this.bindWordingFromCompare();
                    }
                }
            },
        },
    },
    async created() {
        window.rvTrack('View Patent Description', { isDual: this.$route.name === 'subDual' });
        if (this.isShowingAddCommentBox) this.setIsShowingAddCommentBox(false);
        this.wordingService = new WordingService(this.patentId);

        this.showLoading = true;

        // 判断划段权限(大权限 + workspace share权限)
        this.commentPermission = { edit: true, read: true, isOwner: true };
        const { workspace_id, folder_id } = getParams();
        let workspacePermission = {};
        if (workspace_id && folder_id && !this.isFree) {
            this.workspaceId = workspace_id;
            this.folderId = folder_id;
            this.commentService = new CommentService(this.patentId, workspace_id, folder_id);
            try {
                workspacePermission = await this.commentService.getCommentsPermission(workspace_id, folder_id);
            } catch (e) {
                console.log(e);
            }
            this.commentPermission = {
                edit: get(workspacePermission, 'comment.delete'),
                read: get(workspacePermission, 'comment.read'),
                isOwner: get(workspacePermission, 'is_owner') === 'OWNER',
            };
        }
        this.wordingPermission = {
            // commentFlag bo中的设置 用户无法使用comment
            read: this.permissions.commentAndField && !this.permissions.commentFlag && this.commentPermission.read,
            edit: this.permissions.commentAndField && !this.permissions.commentFlag && this.commentPermission.edit,
            semantic: this.permissions.semantic,
        };
        // 监听commentDialog内部事件
        WordingHandler.$on('wordingInCommentClicked', this.scrollToWordingById);
        WordingHandler.$on('commentUpdated', this.commentUpdated);
        WordingHandler.$on('addQuickCommentSuccess', this.cleanMockSelection);
        WordingHandler.$on('sync-with-other-instances', this.resetWordingFromCache);

        (async () => {
            this.patent = await this.getPatentBase(this.patentId);
            this.getAllImagesByPatentId(get(this.patent, 'RELATED_PATENT.desc.patent_id', this.patentId));
        })();

        if (this.isCompareView) {
            await this.compareDeferred?.promise;
            this.descriptionService = new ComparePatentService({
                patentId: this.patentId,
                compareId: this.compareId,
            });
        } else {
            this.descriptionService = new DescriptionService(this.patentId);
        }

        this.serviceInjectMixin_services.add(this.descriptionService);
        const { text, lang, DESC_SOURCE, DESC_SECTIONS } = this.isCompareView ?
            await this.descriptionService.getPatentText({ lang: this.compareLang, type: 'DESC', initParams: this.compareInitParams }):
            await this.descriptionService.getPatentDescription();
        if (this.isCompareView) this.descriptionService.setCompareCompleted(true);
        this.showLoading = false;
        this.defaultOfDesc = text;
        this.langDefaultDesc = lang;

        this.sections = DESC_SECTIONS;
        this.descSource = DESC_SOURCE || {};
        if (this.permissions.commentAndField && !this.permissions.commentFlag) this.updateQuickComment(await QuickCommentService.getQuickComments());
        if (this.isCompareView) {
            this.setAndScrollToSimilar();
            if (this.isCompareSourceColumn) {
                this.bindWordingFromCompare();
            }
        } else {
            this.bindWording();
        }
        this.initSmartImageInText({
            selector: 'desc-part',
            content: 'desc-render',
        });
        // 拉取对比列表
        if (!this.isFree && this.permissions.textCompare && !this.isCompareView) {
            this.descriptionService.getCompareWithList()
                .then(resp => {
                    const _data = get(resp, 'data.data', []);
                    this.compareService = DESC_COMPARE_SERVICE;
                    this.compareData.fullList = this.formatCompareData(_data);
                    this.compareData.targetIdx = 0;
                    this.compareData.fullList.forEach(item => {
                        if (!this.compareData.type[item.compare_with_type]) {
                            this.compareData.type[item.compare_with_type] = [];
                        }
                        this.compareData.type[item.compare_with_type].push(item);
                    });
                    this.compareData.tag = DESC_COMPARE_SERVICE;
                });
        }
    },
    beforeDestroy() {
        this.disposeWording();
        // 解除监听commentDialog内部事件
        WordingHandler.$off('wordingInCommentClicked', this.scrollToWordingById);
        WordingHandler.$off('commentUpdated', this.commentUpdated);
        WordingHandler.$off('addQuickCommentSuccess', this.cleanMockSelection);
        WordingHandler.$off('sync-with-other-instances', this.resetWordingFromCache);
    },
    methods: {
        toggleSideViewSelfTrans(val) {
            this.setSideTransStatus({ side: this.side, isVisible: val });
            this.setUserPreferredSideTransStatus({ side: this.side, isVisible: val });
        },
        onPanelClose() {
            this.panelShow = false;
        },
        showImagePanel(imagePanelData) {
            this.imagePanelData = imagePanelData;
            this.panelShow = true;
            window.rvTrack('Open Image List Panel');
        },
        // 划段高亮相关
        bindWording() {
            this.$nextTick(() => {
                this.paragraphHighlight = new ParagraphHighlight({
                    root: this.$el.querySelector('.desc-render__desc-container .pt-section__body'),
                    lang: this.$i18n.locale,
                    permission: { ...this.wordingPermission, semantic: (this.wordingPermission.semantic && SEMANTIC_SUPPORTED_LANG.includes(this.langDefaultDesc)) },
                    cacheStore: WordingHandler.wordingStorage,
                    cacheKey: `${HIGHLIGHT_CACHE_KEY_IN_DESC}_${this.patentId}`,
                    defaultQuickComments: this.quickCommentList,
                });
                this.paragraphHighlight.$on('create', this.setWording);
                this.paragraphHighlight.$on('remove', this.removeWording);
                this.paragraphHighlight.$on('searchText', this.searchWording);
                this.paragraphHighlight.$on('comment', this.commentWording);
                this.paragraphHighlight.$on('illegal', this.wordingErrorHandler);
                this.resetWordingFromCache();
                this.$emit('onload');
            });
        },
        bindWordingFromCompare() {
            // 只允许左侧进行对比划段
            if (!this.isCompareSourceColumn || !this.enabledCompareWording) return;
            this.disposeWording();
            this.$nextTick(() => {
                this.paragraphHighlight = new ParagraphHighlight({
                    root: this.$el.querySelector('.desc-render__desc-container .pt-section__body'),
                    lang: this.$i18n.locale,
                    permission: { compare: true },
                    cacheStore: WordingHandler.wordingStorage,
                    cacheKey: `${HIGHLIGHT_CACHE_KEY_IN_DESC}_${this.patentId}_${this.compareWordingCacheKey}`,
                    minLength: 10,
                    maxLength: 500,
                    customClass: ['compare-hit'],
                });

                this.paragraphHighlight.$on('compare', async ({
                    text,
                    next,
                }) => {
                    this.changeCompareWordingStart(true);
                    const uniqueId = generateUniqueId();
                    next(uniqueId);
                    const similarNum = await this.onCompareWording(text, uniqueId);
                    this.changeCompareWordingStart(false);
                    // 匹配到相似段落数据
                    if (similarNum) {
                        this.setSimilarWordingTagPosition({
                            side: this.side,
                            content: this.$el,
                            similarNum,
                            uniqueId,
                        });
                    // 未匹配到相似段落数据
                    } else {
                        await this.onShowWordingNotFoundBar();
                        this.paragraphHighlight.updateCommentCount(uniqueId, 0);
                    }
                });

                this.resetWordingFromCache();
                this.$nextTick(() => {
                    this.revertSimilarWordingTagPosition({
                        side: this.side,
                        content: this.$el,
                    });
                    // 如果该tag命中，需要手动active一下
                });
            });
        },
        async setWording(offsetMeta, next) {
            const data = {
                patent_id: this.patentId,
                related_patent_id: this.descRelatedPatentId,
                workspace_id: this.workspaceId,
                folder_id: this.folderId,
                wording_type: 'EMPHASIS',
                source: 'description',
                lang: this.langDefaultDesc,
                start: offsetMeta.startMeta,
                end: offsetMeta.endMeta,
                content: offsetMeta.fullText,
            };
            const { wording_id } = await this.wordingService.setWording(data);
            if (!wording_id) return;
            next(wording_id);
            // 用户引导 8月发版移除
            if (!this.folderId || (this.folderId && this.commentPermission.isOwner)) {
                WordingHandler.$emit('underlineSuccess');
            }
            WordingHandler.$emit('sync-with-other-instances');
            window.rvTrack('Clicked Add Wording Underline', { type: 'description', content: offsetMeta.fullText, isFromWorkspace: !!this.workspaceId });
        },
        async removeWording(removeId, next) {
            const { wording_id } = await this.wordingService.removeWording(removeId);
            if (!wording_id) return;
            next(wording_id);
        },
        async searchWording(text) {
            const { url } = await this.wordingService.semanticSearch(text);
            if (!url) return Toast({ message: this.$t('noSearchResult'), type: 'warning', showClose: true });
            const newWin = window.open('');
            newWin.opener = null;
            newWin.location = `/search/result?${url}`;
            window.rvTrack('Clicked Wording Semantic', { type: 'description', content: text, isFromWorkspace: !!this.workspaceId });
        },
        commentWording({ type, wordingId, offsetMeta, triggerPosition }, next) {
            WordingHandler.updateCommentCallback((...rest) => {
                next(...rest);
                WordingHandler.$emit('sync-with-other-instances');
            });
            if (type === 'add') {
                WordingHandler.updateAddWordingId(wordingId);
                WordingHandler.updateAddOffsetMeta(offsetMeta);
                WordingHandler.updatePatentInfo({
                    patent_id: this.patentId,
                    related_patent_id: this.descRelatedPatentId,
                    workspace_id: this.workspaceId,
                    folder_id: this.folderId,
                    source: 'description',
                    lang: this.langDefaultDesc,
                    quickComment: this.quickCommentList,
                });
                this.setIsShowingAddCommentBox({ display: true, position: triggerPosition });
            } else {
                this.setIsShowingCommentBox(true);
                this.$nextTick(() => {
                    // 定位到comment弹框的对应位置
                    WordingHandler.$emit('clickComment', wordingId);
                });
            }
        },
        wordingErrorHandler(error) {
            console.log(error);
        },
        resetWordingFromCache() {
            // 需要等高亮渲染完成后再执行
            this.$nextTick(() => {
                this.paragraphHighlight && this.paragraphHighlight.resetStorage();
            });
        },
        disposeWording() {
            this.paragraphHighlight && this.paragraphHighlight.dispose();
        },
        cleanMockSelection() {
            this.paragraphHighlight && this.paragraphHighlight.cleanMockSelection();
        },
        scrollToWordingById(comment) {
            if (!comment.wording_id || comment.source !== 'description') return;
            const container = this.wordingScrollContainer;
            const scrollToItem = container.find(`.pt-wording[data-wording-id=${comment.wording_id}]:visible`);
            if (scrollToItem.length === 0) return;
            const positionTop = scrollToItem.offset().top - container.offset().top + container.scrollTop() - 100;
            // 增加延迟来兼容IE系列
            container.animate({ scrollTop: positionTop }, 100);
        },
        commentUpdated({ comment, count }) {
            if (!this.paragraphHighlight) return;
            this.paragraphHighlight.updateCommentCount(comment.wording_id, count);
        },
        previewHook(imgIds, eventType) {
            if (eventType === 'click' && this.neighborName === 'figures') {
                const dualFigRef = this.getDualFigRef();
                const selector = imgIds.map(imgId => `[data-fig-image-id="${imgId}"]`).join(',');
                const imgEle = $(dualFigRef.$el).find(selector).get(0);
                if (imgEle) {
                    dualFigRef.remote_scrollToImg(imgEle);
                    return false;
                }
                return true;
            }
            return true;
        },
        articleClicked(evt) {
            if (!this.textImageFeatureStatus || this.isCompareView) return;
            const eleParent = $(evt.target).parent('.desc-part').get(0);
            const target = evt.target.classList.contains('desc-part') ? evt.target : eleParent;
            const dualFigRef = this.getDualFigRef();
            if (target && target.classList.contains('desc-part')) {
                if (!this.click2figPartActivated) { return; }
                let id;
                const labelIdReg = /([a-f0-9]{2,}-){5}/g;
                Array.from(target.classList).forEach(function(str) {
                    if (labelIdReg.test(str)) {
                        id = str;
                    }
                });
                dualFigRef.remote_scrollToLabelByFigId(id);
            }
            window.rvTrack('Smart Image Clicked Parts');
        },
        // 翻译段落对齐
        setTranslationAlign: debounce(function() {
            if (!this.inSingleView || !this.isOpenTran) return this.initDensityBar();
            console.log('翻译对齐 in desc');
            this.$nextTick(() => {
                this.removeTranslationAlign();

                const originListMatchTag = '.pt-section__body div[p]';
                const transListMatchTag = '.translation-block__body div[p]';
                let originList = [...$(this.$el).find(`${originListMatchTag} .d_n`).parent(), ...$(this.$el).find(`${originListMatchTag} .uspto-container`).parent()];
                let transList = [...$(this.$el).find(`${transListMatchTag} .d_n`).parent(), ...$(this.$el).find(`${transListMatchTag} .uspto-container`).parent()];
                let firstOrigin = null;
                let firstTrans = null;

                if (originList.length && transList.length && originList.length === transList.length) {
                    firstOrigin = $(this.$el).find(`${originListMatchTag}:first`);
                    firstTrans = $(this.$el).find(`${transListMatchTag}:first`);
                } else {
                    originList = $(this.$el).find(originListMatchTag);
                    transList = $(this.$el).find(transListMatchTag);
                }

                setAlignStyle(originList, transList, firstOrigin, firstTrans);
                this.initDensityBar();
            });
        }, 200),
        removeTranslationAlign() {
            const originListMatchTag = '.pt-section__body div[p]';
            const transListMatchTag = '.translation-block__body div[p]';
            let originList = [...$(this.$el).find(`${originListMatchTag} .d_n`).parent(), ...$(this.$el).find(`${originListMatchTag} .uspto-container`).parent()];
            let transList = [...$(this.$el).find(`${transListMatchTag} .d_n`).parent(), ...$(this.$el).find(`${transListMatchTag} .uspto-container`).parent()];
            let firstOrigin = null;
            let firstTrans = null;

            if (originList.length && transList.length && originList.length === transList.length) {
                firstOrigin = $(this.$el).find(`${originListMatchTag}:first`);
                firstTrans = $(this.$el).find(`${transListMatchTag}:first`);
            } else {
                originList = $(this.$el).find(originListMatchTag);
                transList = $(this.$el).find(transListMatchTag);
            }

            removeAlignStyle(originList, transList, firstOrigin, firstTrans);
        },
        setAndScrollToSimilar() {
            this.$nextTick(() => {
                this.setSimilarParaTagPosition({
                    side: this.side,
                    content: this.$el,
                });
                if (this.isScrollImmediately[this.side]) {
                    this.changeScrollImmediately({
                        side: this.side,
                        status: false,
                    });
                    const params = this.getCurScrollInfo(this.side);
                    this.scrollToSimilarPara(params);
                }
            });
        },
        getDualFigRef() {
            return this.neighborSide === 'left' ? this.leftRef : this.rightRef;
        },
    },
};
</script>

<style lang="scss" src="./desc.scss"></style>
